/**
 * Photos a participant submitted from Pathfinder Tracker, on the mission map.
 *
 * Field reports already store the geotagged JPEG. Until these pins existed
 * they only showed up on an Intel Packet evidence tab — the mission map drew
 * the route and our devices, and nowhere the photo was taken.
 */
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Marker, Popup } from 'react-map-gl';
import {
  DARK_MAP_POPUP_CLASS,
  DarkMapPopupCoords,
  DarkMapPopupRow,
  DarkMapPopupShell,
  useDarkMapPopupChrome,
} from '../../../components/map/DarkMapPopup';
import { fetchFieldReportPhoto } from '../../../services/intelPacketApi';
import { MissionFieldReport, listMissionFieldReports } from '../../../services/missionApi';
import { formatWhen } from '../format';
import { useMapLayerVisible } from '../../map/mapLayerVisibility';
import { photoAccent, photoDescription, photoTitle } from '../photoView';
import { SubjectDossierDialog } from '../../subjects/SubjectDossier';
import { usePhotoSubject } from '../../subjects/usePhotoSubject';
import FieldPhotoReview from '../FieldPhotoReview';

export interface MissionMediaLayerProps {
  missionId: number;
  refreshKey?: number;
  interactive?: boolean;
  onSelect?: (latitude: number, longitude: number) => void;
}

const MissionMediaLayer: React.FC<MissionMediaLayerProps> = ({
  missionId,
  refreshKey,
  interactive = true,
  onSelect,
}) => {
  const [reports, setReports] = useState<MissionFieldReport[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const visible = useMapLayerVisible('evidence');
  const dossier = usePhotoSubject({ missionId });

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      listMissionFieldReports(missionId)
        .then((rows) => { if (!cancelled) setReports(rows); })
        .catch(() => { if (!cancelled) setReports([]); });
    };
    load();
    const timer = window.setInterval(load, 15000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [missionId, refreshKey]);

  if (!visible) return null;

  return (
    <>
      {reports.map((report) => (
        <PhotoMarker
          key={report.id}
          report={report}
          open={openId === report.id}
          interactive={interactive}
          onOpen={() => {
            setOpenId(report.id);
            onSelect?.(report.latitude, report.longitude);
          }}
          onClose={() => setOpenId((id) => (id === report.id ? null : id))}
          onOpenCard={() => { dossier.open(report.id); }}
        />
      ))}
      <SubjectDossierDialog
        open={!!dossier.card}
        card={dossier.card}
        scope={{ missionId }}
        initialView="quad"
        onClose={dossier.close}
        onSaved={dossier.onSaved}
        onError={dossier.setError}
      />
    </>
  );
};

const PhotoMarker: React.FC<{
  report: MissionFieldReport;
  open: boolean;
  interactive: boolean;
  onOpen: () => void;
  onClose: () => void;
  onOpenCard: () => void;
}> = ({ report, open, interactive, onOpen, onClose, onOpenCard }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const accent = photoAccent(report);
  useDarkMapPopupChrome(open);

  useEffect(() => {
    if ((!open && !preview) || !report.has_photo) {
      setSrc(null);
      return undefined;
    }
    let cancelled = false;
    fetchFieldReportPhoto(report.id, 'original')
      .then((url) => { if (!cancelled) setSrc(url); })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; };
  }, [open, preview, report.has_photo, report.id]);

  return (
    <>
      <Marker
        longitude={report.longitude}
        latitude={report.latitude}
        anchor="center"
        style={{ pointerEvents: interactive ? 'auto' : 'none' }}
      >
        <Box
          role="button"
          tabIndex={interactive ? 0 : -1}
          aria-label={report.notes || report.kind || 'Field photo'}
          onClick={interactive ? onOpen : undefined}
          onKeyDown={(e) => {
            if (!interactive) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen();
            }
          }}
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(11,16,23,0.92)',
            color: accent,
            border: `2px solid ${accent}`,
            boxShadow: `0 0 8px ${accent}88`,
            cursor: interactive ? 'pointer' : 'default',
          }}
        >
          <PhotoCameraIcon sx={{ fontSize: 14 }} />
        </Box>
      </Marker>
      {open && (
        <Popup
          longitude={report.longitude}
          latitude={report.latitude}
          anchor="bottom"
          offset={16}
          onClose={onClose}
          closeButton={false}
          closeOnClick={false}
          className={DARK_MAP_POPUP_CLASS}
          maxWidth="300px"
          style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
        >
          <DarkMapPopupShell
            accent={accent}
            title={photoTitle(report)}
            badge={report.watchlisted ? 'WATCHLIST' : 'PHOTO'}
            onClose={onClose}
          >
            {report.device_id && (
              <DarkMapPopupRow label="Device" mono>{report.device_id}</DarkMapPopupRow>
            )}
            {report.device_owner && (
              <DarkMapPopupRow label="From">{report.device_owner}</DarkMapPopupRow>
            )}
            {report.observed_at && (
              <DarkMapPopupRow label="When">{formatWhen(report.observed_at)}</DarkMapPopupRow>
            )}
            {report.kind && (
              <DarkMapPopupRow label="Kind">{report.kind}</DarkMapPopupRow>
            )}
            {photoDescription(report) && (
              <DarkMapPopupRow label="Scene">{photoDescription(report)}</DarkMapPopupRow>
            )}
            {report.ocr_text && (
              <DarkMapPopupRow label="OCR">{report.ocr_text}</DarkMapPopupRow>
            )}
            {(report.objects || []).length > 0 && (
              <DarkMapPopupRow label="Objects">{report.objects!.join(', ')}</DarkMapPopupRow>
            )}
            {(report.face_matches || []).length > 0 && (
              <DarkMapPopupRow label="Faces">{report.face_matches!.join(', ')}</DarkMapPopupRow>
            )}
            {report.notes && (
              <DarkMapPopupRow label="Notes">{report.notes}</DarkMapPopupRow>
            )}
            {src ? (
              <img
                src={src}
                alt={report.notes || 'Field photo'}
                role="button"
                title="Review photo"
                onClick={() => setPreview(true)}
                style={{
                  display: 'block',
                  width: '100%',
                  maxHeight: 180,
                  objectFit: 'cover',
                  marginTop: 8,
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.08)',
                  cursor: 'pointer',
                }}
              />
            ) : (
              <DarkMapPopupRow label="Photo">
                {report.has_photo ? 'Loading…' : 'None attached'}
              </DarkMapPopupRow>
            )}
            <DarkMapPopupCoords lat={report.latitude} lon={report.longitude} />
            <Box
              role="button"
              tabIndex={0}
              onClick={onOpenCard}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenCard(); }
              }}
              sx={{
                mt: 1,
                height: 26,
                px: 1.1,
                borderRadius: 0.75,
                border: '1px solid #333a45',
                color: '#e8ebee',
                fontSize: '0.68rem',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              Open card
            </Box>
          </DarkMapPopupShell>
        </Popup>
      )}
      <FieldPhotoReview
        open={preview}
        src={src}
        title={photoTitle(report)}
        alt={report.notes || 'Field photo'}
        onClose={() => setPreview(false)}
      />
    </>
  );
};

export default MissionMediaLayer;
